/* eslint-disable */
import { EvesTextbox } from "@/common/components/atoms/textbox/textbox";
import { FlipCard } from "@/common/components/molecules/flipCard/flipCard";
import { requiredFields } from "@/modules/shared/schemas/priceFormSchema";
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import {
  FormControlLabel,
  Grid,
  InputAdornment,
} from "@mui/material";
import React, { RefObject, useEffect, useRef, useState } from "react";
import { Controller } from "react-hook-form";
import { withContext } from "@/common/utils/withContext";
import EvesSelect from "@/common/components/atoms/select/select";
import { EvesCheckbox } from "@/common/components/atoms/checkbox/checkbox";
import { useQuery } from "react-query";
import DefaultAvatar from "@/assets/img/user-default.png";
import { getUserImage } from "@/services/userProfile";
import {
  blobToBase64String,
  isActive,
  isAdmin,
} from "@/common/utils/utils";
import { getComponentValueSafe } from "@/common/utils/componentListHelper";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { IUserContextModel } from "@/store/auth/types";
import Config from "@/common/constants/config";
import { ComponentKeys } from "@/common/enums/enums";

const UserProfile = (props: any) => {
  const { userForm, isUpdate, userId } = props;
  const { t: translate } = useTranslation();
  const userInfoContext: IUserContextModel = useSelector(
    (state: any) => state.userContext
  );
  const includeRole = userInfoContext.userInfo.includeRole;
  const [state, setStateData] = useState({
    userData: {},
    avatar: "",
    phoneNumberError: false,
    phoneNumberErrorText: "",
    mobileNumberError: false,
    mobileNumberErrorText: "",
  } as any);
  const isBillingComponentActive = isActive(getComponentValueSafe(ComponentKeys.BILLING));
  const isUserAdmin = isAdmin();

  // EVES-4200
  // Author: Abhishek Bharti
  // Date: 19-12-2025
  // Description: Show role dropdown if includeRole array is available in user info
  const isIncludeRoleAvailable = includeRole && includeRole.length > 0;
  const maxPictureKb: number = Config.user.maxPictureKb;
  const hasFetchedUserImageRef = useRef(false);

  const { refetch: fetchUserImage } = useQuery(
    "user-image",
    () => {
      return getUserImage(props.userId);
    },
    {
      enabled: false,
      onSuccess: async (data: any) => {
        if (data) {
          const base64String = await blobToBase64String(data);
          setStateData((currentData) => {
            return {
              ...currentData,
              avatar: base64String || DefaultAvatar,
            };
          });
        }
      },
    }
  );

  useEffect(() => {
    if (userId && !hasFetchedUserImageRef.current) {
      hasFetchedUserImageRef.current = true;
      fetchUserImage();
    }
  }, [userId, fetchUserImage]);

  const onLanguageChange = () => {
    const formValues = userForm.getValues();
    userForm.setValue("language", formValues.language);
  };

  const onStatusChange = () => {
    const formValues = userForm.getValues();
    userForm.setValue("status", formValues.status);
  };

  const onRoleChange = () => {
    const formValues = userForm.getValues();
    props.onChangeRole(formValues.role);
  };

  const inputOpenFileRef: RefObject<HTMLInputElement> = React.createRef();
  const onFileSelected = (event: any) => {
    if (event.target.files && event.target.files[0]) {
      if (event.target.files[0].size < maxPictureKb * 1024) {
        const reader = new FileReader();
        reader.onload = () => {
          userForm.setValue("image", reader.result as unknown as string, {
            shouldDirty: true,
          });
          setStateData((currentData: any) => {
            return {
              ...currentData,
              avatar: reader.result,
            };
          });
        };
        reader.readAsDataURL(event.target.files[0]);
      } else {
        props.notificationService?.notify({
          message: `${translate("users.picture_size_error").replace(
            /{{maxPictureKb}}/g,
            maxPictureKb.toString()
          )}`,
          type: "error",
        });
      }
    }
  };

  const onFileBrowseClick = () => {
    if (!userForm.getValues().name) {
      userForm.setValue("name", "", {
        shouldTouch: true,
        shouldValidate: true,
      });
    }
    inputOpenFileRef?.current?.click();
  };

  const onFileRemoveClick = () => {
    setStateData((currentData: any) => {
      return {
        ...currentData,
        avatar: DefaultAvatar,
      };
    });
  };

  const assignedSites = userForm.watch("assignedSites") || [];

  return (
    <>
      <Grid container rowSpacing={1} columnSpacing={2}>
        {/* HEADER SECTION */}
        <Grid item xs={3}>
          <FlipCard
            height={"100px"}
            data-cy="user-image-card"
            logo={state.avatar}
            inputOpenFileRef={inputOpenFileRef}
            onFileSelected={onFileSelected}
            onFileBrowseClick={onFileBrowseClick}
            onFileRemoveClick={onFileRemoveClick}
            isUpdate={!state.isDisabled}
          />
        </Grid>
        <Grid item xs={9}>
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <Controller
                name="name"
                control={userForm.control}
                render={({ field }) => (
                  <EvesTextbox
                    {...field}
                    id="name"
                    label={`${translate("users.last_name")}`}
                    data-cy="user-name"
                    fullWidth
                    disabled={state.isDisabled}
                    required={requiredFields["name"]}
                    variant="standard"
                    error={!!userForm.formState?.errors.name}
                    helperText={translate(userForm.formState?.errors.name?.message || " ")}
                    autoFocus
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="firstName"
                control={userForm.control}
                render={({ field }) => (
                  <EvesTextbox
                    {...field}
                    id="firstName"
                    label={`${translate("users.first_name")}`}
                    data-cy="user-first-name"
                    fullWidth
                    disabled={state.isDisabled}
                    required={true}
                    onChange={(event: any) => {
                      field.onChange(event);
                      userForm.trigger("firstName");
                    }}
                    variant="standard"
                    error={!!userForm.formState?.errors.firstName}
                    helperText={translate(userForm.formState?.errors.firstName?.message || " ")}
                  />
                )}
              />
            </Grid>
          </Grid>
        </Grid>

        {/* TWO COLUMN FORM BODY */}
        <Grid item xs={12}>
          <Grid container columnSpacing={2}>

            {/* LEFT COLUMN */}
            <Grid item xs={6}>
              <Grid container direction="column" rowSpacing={1}>
                <Grid item>
                  <Controller
                    name="email"
                    control={userForm.control}
                    render={({ field }) => (
                      <EvesTextbox
                        {...field}
                        label={`${translate("users.email")} *`}
                        fullWidth
                        variant="standard"
                        error={!!userForm.formState?.errors.email}
                        helperText={translate(userForm.formState?.errors.email?.message || " ")}
                      />
                    )}
                  />
                </Grid>
                <Grid item>
                  <Controller
                    name="language"
                    control={userForm.control}
                    render={({ field }) => (
                      <EvesSelect
                        label={`${translate("users.locale")}`}
                        options={props.languageOptions}
                        {...field}
                        onChange={(event: any) => {
                          field.onChange(event);
                          onLanguageChange();
                        }}
                      />
                    )}
                  />
                </Grid>
                {isIncludeRoleAvailable && (
                  <Grid item>
                    <Controller
                      name="role"
                      control={userForm.control}
                      render={({ field }) => (
                        <EvesSelect
                          label={`${translate("users.role")}`}
                          options={props.userRoleOptions}
                          {...field}
                          disabled={props.isRoleDisabled}
                          onChange={(event: any) => {
                            field.onChange(event);
                            onRoleChange();
                          }}
                        />
                      )}
                    />
                  </Grid>
                )}
                <Grid item>
                  <Controller
                    name="mobile"
                    control={userForm.control}
                    render={({ field }) => (
                      <EvesTextbox
                        {...field}
                        label={`${translate("users.mobile")}`}
                        fullWidth
                        variant="standard"
                        error={!!userForm.formState?.errors.mobile}
                        helperText={translate(userForm.formState?.errors.mobile?.message || " ")}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* RIGHT COLUMN */}
            <Grid item xs={6}>
              <Grid container direction="column" rowSpacing={1}>
                {!props.isSiteSelectionHidden && (
                  <Grid item>
                    <Controller
                      name="assignedSites"
                      control={userForm.control}
                      render={({ field }) => {
                        // const sites = userForm.watch("assignedSites") || [];
                        const siteDisplayValue = props.isSiteSelectionDisabled
                          ? translate("general.all_sites")
                          : assignedSites.map((s: any) => s.name || s.id).join(", ");
                        return (
                          <EvesTextbox
                            {...field}
                            label={`${translate("users.sites")} *`}
                            fullWidth
                            readOnly
                            variant="standard"
                            onClick={() => !props.isSiteSelectionDisabled && props.onOpenSiteLookup?.()}
                            value={siteDisplayValue}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  <ArrowDropDownIcon />
                                </InputAdornment>
                              ), style: { cursor: props.isSiteSelectionDisabled ? 'default' : 'pointer' }
                            }}
                          />
                        );
                      }}
                    />
                  </Grid>
                )}
                {isUpdate && props.statusRequired && (
                  <Grid item>
                    <Controller
                      name="status"
                      control={userForm.control}
                      render={({ field }) => (
                        <EvesSelect
                          required={true}
                          label={`${translate("users.status")}`}
                          options={props.statusOptions}
                          {...field}
                          onChange={(event: any) => {
                            field.onChange(event);
                            onStatusChange();
                          }}
                        />
                      )}
                    />
                  </Grid>
                )}
                <Grid item>
                  <Controller
                    name="phone"
                    control={userForm.control}
                    render={({ field }) => (
                      <EvesTextbox
                        {...field}
                        label={`${translate("users.phone")}`}
                        fullWidth
                        variant="standard"
                        error={!!userForm.formState?.errors.phone}
                        helperText={translate(userForm.formState?.errors.phone?.message || " ")}
                      />
                    )}
                  />
                </Grid>
                {isUserAdmin && (
                  <Grid item>
                    <Controller
                      name="plateID"
                      control={userForm.control}
                      render={({ field }) => (
                        <EvesTextbox
                          {...field}
                          label={`${translate("users.plate_id")}`}
                          fullWidth
                          variant="standard"
                          error={!!userForm.formState?.errors.plateID}
                          helperText={translate(userForm.formState?.errors.plateID?.message || " ")}
                        />
                      )}
                    />
                  </Grid>
                )}
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        {/* FOOTER CHECKBOXES */}
        <Grid item xs={12}>
          {isBillingComponentActive && !props.isFreeAccessDisabled && (
            <Controller
              name="freeAccess"
              control={userForm.control}
              render={({ field }) => (
                <FormControlLabel
                  control={<EvesCheckbox {...field} checked={userForm.watch("freeAccess")} />}
                  label={translate("users.allow_free_access")}
                />
              )}
            />
          )}
          {!props.isTechnicalDisabled && (
            <Controller
              name="technical"
              control={userForm.control}
              render={({ field }) => (
                <FormControlLabel
                  control={<EvesCheckbox {...field} checked={userForm.watch("technical")} />}
                  label={`${translate("users.technical")}`}
                />
              )}
            />
          )}
        </Grid>
      </Grid>
    </>
  );
};

export default withContext(UserProfile);
